import { captureAttribution, clientId } from './attribution';
import { clearConsent, readConsent, writeConsent, type ConsentState } from './consent';
import { vendorsPermitted } from './host';
import { MEASUREMENT_IDS, vendorConfig, type MeasurementIds } from './ids';
import { browserTimeZone, consentRequired } from './region';
import { loadVendors, sendEvent } from './vendors';

/**
 * Measurement, consent and page views for the SPA — the port of omega-measure.js
 * (docs/behavior/measure-legal-auth.md §1) with the rulings ML-D1, ML-D2 and ML-D6 applied.
 *
 * Doctrine kept from production:
 *  1. No vendor tag loads before consent where consent is required. Not one request.
 *  2. Attribution is first-party and captured regardless of consent.
 *  3. An empty vendor id is a no-op, never an error.
 *  4. Nothing here throws into the page.
 *  5. Vendors load and hits leave only on the production hosts (./host.ts); elsewhere every step
 *     runs identically except the vendor script injection and the send.
 */

export type MeasurementEvent =
  'page_view' | 'view_item' | 'add_to_cart' | 'begin_checkout' | 'lead' | 'sign_up' | 'purchase' | 'vault_download';

export type EventParams = Readonly<Record<string, unknown>>;

interface QueuedEvent {
  readonly name: MeasurementEvent;
  readonly params: EventParams;
}

/** Events kept while consent is pending; later ones are dropped (production limit). */
export const EVENT_QUEUE_LIMIT = 40;
/** Page views are read this long after a navigation, once the new page has set its title. */
export const PAGE_VIEW_DELAY_MS = 60;

export interface MeasurementOptions {
  readonly ids?: MeasurementIds;
  /** IANA time zone reader; may throw when the zone is undetectable. */
  readonly timeZone?: () => string;
  readonly now?: () => number;
  readonly referrer?: () => string;
  /** Subscribes to in-app navigations; returns an unsubscribe function. */
  readonly onNavigate?: (listener: () => void) => () => void;
  /** Document host name, read once when vendors would first load or send (default `location.hostname`). */
  readonly hostname?: () => string;
}

export interface ConsentApi {
  /** 'granted' | 'denied' | 'unset', read from shared storage each time. */
  state: () => ConsentState;
  /** Records consent, loads vendors, flushes queued events, closes the banner. */
  grant: () => void;
  /** Records refusal and closes the banner. Already-loaded scripts stay until the next load. */
  deny: () => void;
  /** Forgets the choice and shows the banner, in any region (the Cookie choice button). */
  revoke: () => void;
  /** Banner visibility, for useSyncExternalStore. */
  isBannerOpen: () => boolean;
  subscribe: (listener: () => void) => () => void;
}

export interface Measurement {
  init: () => void;
  track: (name: MeasurementEvent, params?: EventParams) => void;
  consent: ConsentApi;
  /** Tears down listeners and timers (tests). */
  dispose: () => void;
}

export function createMeasurement(options: MeasurementOptions = {}): Measurement {
  const config = vendorConfig(options.ids ?? MEASUREMENT_IDS);
  const readTimeZone = options.timeZone ?? browserTimeZone;
  const now = options.now ?? Date.now;
  const referrer = options.referrer ?? (() => document.referrer);
  const readHostname = options.hostname ?? (() => window.location.hostname);
  let permitted: boolean | undefined;
  /** Host guard, evaluated once per instance. */
  const vendorsAllowed = (): boolean => (permitted ??= vendorsPermitted(readHostname()));
  const send = (name: MeasurementEvent, params: EventParams): void => {
    if (vendorsAllowed()) sendEvent(config, name, params);
  };

  let booted = false;
  let vendorsLoaded = false;
  let bannerOpen = false;
  let lastPage: string | null = null;
  let pageViewTimer: ReturnType<typeof setTimeout> | undefined;
  let stopNavigation: (() => void) | undefined;
  const queue: QueuedEvent[] = [];
  const bannerListeners = new Set<() => void>();

  function setBanner(open: boolean): void {
    if (bannerOpen === open) return;
    bannerOpen = open;
    bannerListeners.forEach((listener) => {
      listener();
    });
  }

  /** ML-D1/ML-D2: queued events are sent whenever consent is granted and vendors are live. */
  function flush(): void {
    if (!vendorsLoaded || readConsent() !== 'granted') return;
    for (const event of queue.splice(0, queue.length)) send(event.name, event.params);
  }

  function ensureVendors(): void {
    if (!vendorsLoaded) {
      vendorsLoaded = true;
      if (vendorsAllowed()) loadVendors(config);
    }
    flush();
  }

  function record(state: 'granted' | 'denied'): void {
    writeConsent(state, now());
    if (state === 'granted') ensureVendors();
  }

  function track(name: MeasurementEvent, params: EventParams = {}): void {
    // Queue until consent is granted AND the vendors exist (ML-D1: never send into a missing gtag).
    if (readConsent() !== 'granted' || !vendorsLoaded) {
      if (queue.length < EVENT_QUEUE_LIMIT) queue.push({ name, params });
      return;
    }
    send(name, params);
  }

  function pageView(): void {
    const page = window.location.pathname + window.location.search;
    if (page === lastPage) return;
    lastPage = page;
    track('page_view', { page_path: window.location.pathname, page_title: document.title });
  }

  function schedulePageView(): void {
    clearTimeout(pageViewTimer);
    pageViewTimer = setTimeout(pageView, PAGE_VIEW_DELAY_MS);
  }

  function init(): void {
    if (booted) return;
    booted = true;
    try {
      captureAttribution({
        search: window.location.search,
        pathname: window.location.pathname,
        referrer: referrer(),
        now: now(),
      });
      clientId(now());
    } catch (error) {
      console.warn('measurement: attribution not captured', error);
    }
    if (options.onNavigate) stopNavigation = options.onNavigate(schedulePageView);

    const state = readConsent();
    if (state === 'granted') ensureVendors();
    else if (state === 'denied') {
      /* respected: nothing loads */
    } else if (consentRequired(readTimeZone)) setBanner(true);
    else record('granted');

    // The landing page view waits for the page title the shell sets on first render (ML-D13).
    schedulePageView();
  }

  const consent: ConsentApi = {
    state: readConsent,
    grant: () => {
      record('granted');
      setBanner(false);
    },
    deny: () => {
      record('denied');
      setBanner(false);
    },
    revoke: () => {
      clearConsent();
      setBanner(true);
    },
    isBannerOpen: () => bannerOpen,
    subscribe: (listener) => {
      bannerListeners.add(listener);
      return () => {
        bannerListeners.delete(listener);
      };
    },
  };

  return {
    init,
    track,
    consent,
    dispose: () => {
      clearTimeout(pageViewTimer);
      stopNavigation?.();
      bannerListeners.clear();
    },
  };
}
